/**
 * CatalogChangesPage — Phase 24 / Task 24.9 (DEC-100). Hauler operator-change
 * review (existing-hauler "/catalog-changes"). Lists PENDING reviews that Paelode
 * approved which touch this hauler's tank batteries; the DISPATCH_MANAGER acts on
 * each (Accept = re-link operator, Dismiss = keep config, Defer = later).
 * Accepted/dismissed/deferred items move to the History tab. Plain English — no
 * UUIDs shown. DISPATCH_MANAGER+ (nav-gated; server-gated MANAGE_HAULER_CATALOG).
 */
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import {
  useReviews,
  useReviewAction,
  reviewActionError,
  type HaulerReview,
  type HaulerAction,
} from '../features/catalog-changes/hooks';

const PENDING = new Set(['PENDING']);

function ChangeSummary({ r }: { r: HaulerReview }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={{ fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>
        {r.wellName ?? 'Unknown well'}
        {r.apiNumber !== null ? (
          <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}>
            {' '}
            · {r.apiNumber}
          </span>
        ) : null}
      </div>
      {r.changeType === 'OPERATOR_ID_CHANGED' ? (
        <div style={{ fontSize: '14px' }}>
          <span style={{ color: 'var(--color-text-secondary)' }}>
            {r.oldOperatorName ?? 'Previous operator'}
          </span>
          <span style={{ margin: '0 8px', color: 'var(--color-accent-blue)' }}>→</span>
          <span style={{ fontWeight: 600 }}>{r.newOperatorName ?? 'New operator'}</span>
        </div>
      ) : (
        <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          {r.changeType.replace(/_/g, ' ').toLowerCase()}
        </div>
      )}
      {r.paelodeApprovedAt !== null ? (
        <div style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
          Approved {new Date(r.paelodeApprovedAt).toLocaleDateString()}
        </div>
      ) : null}
      {r.affectedTankBatteries.length > 0 ? (
        <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
          Tank batteries: {r.affectedTankBatteries.join(', ')}
        </div>
      ) : null}
    </div>
  );
}

export function CatalogChangesPage(): React.JSX.Element {
  const { data, isLoading } = useReviews();
  const action = useReviewAction();
  const [tab, setTab] = React.useState<'pending' | 'history'>('pending');

  const reviews = data?.reviews ?? [];
  const pending = reviews.filter((r) => PENDING.has(r.reviewStatus));
  const history = reviews.filter((r) => !PENDING.has(r.reviewStatus));
  const shown = tab === 'pending' ? pending : history;

  const run = (id: string, act: HaulerAction): void => {
    action.mutate(
      { id, action: act },
      {
        onSuccess: () =>
          toast.success(
            act === 'accept'
              ? 'Operator change accepted'
              : act === 'dismiss'
                ? 'Change dismissed'
                : 'Deferred for later',
          ),
        onError: (err) => toast.error(reviewActionError(err), { duration: 12_000 }),
      },
    );
  };

  return (
    <div style={{ padding: '20px', maxWidth: '900px' }}>
      <h1 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '4px' }}>Operator Changes</h1>
      <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>
        Wells you haul from had operator changes confirmed by lodeX Pro. Review each below.
      </p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {(['pending', 'history'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={(): void => setTab(t)}
            style={{
              fontSize: '13px',
              padding: '6px 12px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--color-border-secondary)',
              cursor: 'pointer',
              backgroundColor: tab === t ? 'var(--color-accent-blue)' : 'transparent',
              color: tab === t ? 'var(--color-on-accent)' : 'var(--color-text-secondary)',
            }}
          >
            {t === 'pending' ? `Pending (${pending.length})` : `History (${history.length})`}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
      ) : shown.length === 0 ? (
        <p style={{ color: 'var(--color-text-secondary)' }}>
          {tab === 'pending' ? 'No operator changes need your review.' : 'No reviewed changes yet.'}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {shown.map((r) => (
            <div
              key={r.id}
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-card)',
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <ChangeSummary r={r} />
              {tab === 'pending' ? (
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <Button
                    type="button"
                    disabled={action.isPending}
                    onClick={(): void => run(r.id, 'accept')}
                  >
                    Accept
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={action.isPending}
                    onClick={(): void => run(r.id, 'dismiss')}
                  >
                    Dismiss
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={action.isPending}
                    onClick={(): void => run(r.id, 'defer')}
                  >
                    Defer
                  </Button>
                </div>
              ) : (
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 700,
                    flexShrink: 0,
                    color:
                      r.reviewStatus === 'ACCEPTED'
                        ? 'var(--color-success)'
                        : 'var(--color-text-tertiary)',
                  }}
                >
                  {r.reviewStatus}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
